import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { Progress } from '../../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import Sidebar from '../../components/layout/Sidebar';
import Header from '../../components/layout/Header';
import Avatar from '../../components/common/Avatar';
import TaskSchedulerDialog from '../../components/tasks/TaskSchedulerDialog';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import { useTimer } from '../../contexts/TimerContext';
import { cn } from '../../lib/utils';
import { 
  ArrowLeft,
  Clock,
  Calendar,
  CalendarPlus,
  User,
  CheckCircle2,
  XCircle,
  MessageSquare,
  Paperclip,
  Upload,
  Send,
  FileText,
  Image,
  Download,
  Trash2,
  Loader2,
  Play,
  Pause,
  Square,
  AlertCircle,
  Check,
  X,
  Layers,
  Plus,
  Eye,
  Star
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/axios';

const TaskDetailPage = () => {
  const { taskId } = useParams();
  const navigate = useNavigate();
  const { theme } = useTheme();
  const { user } = useAuth();
  const { activeTimers, startTimer, stopTimer, loading: timerLoading } = useTimer();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [task, setTask] = useState(null);
  const [project, setProject] = useState(null);
  const [messages, setMessages] = useState([]);
  const [versions, setVersions] = useState([]);
  const [files, setFiles] = useState([]);
  
  // Chat
  const [newMessage, setNewMessage] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  const messagesEndRef = useRef(null);
  
  // Version dialog
  const [showVersionDialog, setShowVersionDialog] = useState(false);
  const [versionNote, setVersionNote] = useState('');
  const [versionFiles, setVersionFiles] = useState([]);
  const [submittingVersion, setSubmittingVersion] = useState(false);
  
  // File validation
  const [validatingFile, setValidatingFile] = useState(null);
  
  // File upload
  const [uploadingFile, setUploadingFile] = useState(false);
  const fileInputRef = useRef(null);
  
  // Client validation by agency modal
  const [showClientValidationModal, setShowClientValidationModal] = useState(false);
  const [validationChannel, setValidationChannel] = useState('email');
  const [validationNote, setValidationNote] = useState('');
  const [validatingOnBehalf, setValidatingOnBehalf] = useState(false);
  
  // Task scheduler dialog
  const [showSchedulerDialog, setShowSchedulerDialog] = useState(false);
  
  // Check if there's an active timer for this task
  const activeTimerForTask = activeTimers.find(t => t.task_id === taskId);
  const hasActiveTimer = !!activeTimerForTask;
  
  const isAgency = user?.role === 'agency' || user?.role === 'project_manager';
  const isClient = user?.role === 'client';
  const isMember = user?.role === 'team_member';

  // Timer handlers
  const handleStartTimer = async () => {
    try {
      await startTimer(taskId, task?.project_id);
      toast.success('Chronomètre démarré');
    } catch (error) {
      toast.error('Erreur lors du démarrage du chronomètre');
    }
  };

  const handleStopTimer = async () => {
    if (activeTimerForTask) {
      try {
        await stopTimer(activeTimerForTask.id);
        toast.success('Chronomètre arrêté');
      } catch (error) {
        toast.error('Erreur lors de l\'arrêt du chronomètre');
      }
    }
  };

  useEffect(() => {
    fetchTaskData();
  }, [taskId]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const fetchTaskData = async () => {
    try {
      setLoading(true);
      const [taskRes, messagesRes, versionsRes, filesRes] = await Promise.all([
        api.get(`/tasks/${taskId}`),
        api.get(`/tasks/${taskId}/messages`).catch(() => ({ data: [] })),
        api.get(`/tasks/${taskId}/versions`).catch(() => ({ data: [] })),
        api.get(`/tasks/${taskId}/files`).catch(() => ({ data: [] }))
      ]);
      
      setTask(taskRes.data);
      setMessages(messagesRes.data || []);
      setVersions(versionsRes.data || []);
      setFiles(filesRes.data || []);
      
      // Get project info
      if (taskRes.data?.project_id) {
        const projectRes = await api.get(`/projects/${taskRes.data.project_id}`);
        setProject(projectRes.data);
      }
    } catch (error) {
      console.error('Error fetching task:', error);
      toast.error('Erreur lors du chargement de la tâche');
    } finally {
      setLoading(false);
    }
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim()) return;
    
    setSendingMessage(true);
    try {
      await api.post(`/tasks/${taskId}/messages`, {
        content: newMessage,
        type: 'message'
      });
      setNewMessage('');
      fetchTaskData();
    } catch (error) {
      toast.error('Erreur lors de l\'envoi du message');
    } finally {
      setSendingMessage(false);
    }
  };

  const handleSubmitVersion = async () => {
    setSubmittingVersion(true);
    try {
      const formData = new FormData();
      formData.append('note', versionNote);
      versionFiles.forEach(file => {
        formData.append('files', file);
      });
      
      await api.post(`/tasks/${taskId}/versions`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success('Version soumise avec succès');
      setShowVersionDialog(false);
      setVersionNote('');
      setVersionFiles([]);
      fetchTaskData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la soumission');
    } finally {
      setSubmittingVersion(false);
    }
  };

  const handleAgencyValidate = async () => {
    try {
      await api.put(`/tasks/${taskId}/validate/agency`);
      toast.success('Tâche validée par l\'agence');
      fetchTaskData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la validation');
    }
  };

  const handleClientValidate = async (approved) => {
    try {
      await api.put(`/tasks/${taskId}/validate/client`, { approved });
      toast.success(approved ? 'Tâche validée !' : 'Révisions demandées');
      fetchTaskData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la validation');
    }
  };

  const handleValidateFile = async (fileId, validated, by) => {
    try {
      await api.put(`/tasks/${taskId}/files/${fileId}/validate`, {
        validated,
        validated_by: by
      });
      toast.success(validated ? 'Fichier validé' : 'Fichier rejeté');
      fetchTaskData();
    } catch (error) {
      toast.error('Erreur lors de la validation du fichier');
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setUploadingFile(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      await api.post(`/tasks/${taskId}/files`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success('Fichier uploadé avec succès');
      fetchTaskData();
    } catch (error) {
      toast.error('Erreur lors de l\'upload du fichier');
    } finally {
      setUploadingFile(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleClientValidationByAgency = async () => {
    if (!validationChannel) {
      toast.error('Veuillez sélectionner un canal de validation');
      return;
    }
    
    setValidatingOnBehalf(true);
    try {
      await api.put(`/tasks/${taskId}/validate/client-by-agency`, {
        approved: true,
        channel: validationChannel,
        note: validationNote
      });
      
      const channelLabels = {
        email: 'par email',
        phone: 'par téléphone',
        in_agency: 'en agence'
      };
      
      toast.success(`Tâche validée au nom du client (${channelLabels[validationChannel]})`);
      setShowClientValidationModal(false);
      setValidationChannel('email');
      setValidationNote('');
      fetchTaskData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la validation');
    } finally {
      setValidatingOnBehalf(false);
    }
  };

  const handleCreateVersionFromFile = async (file) => {
    const versionNumber = versions.length + 1;
    
    if (!window.confirm(`Créer la version V${versionNumber} à partir du fichier "${file.name}" ?`)) return;
    
    try {
      // Create a new version entry linked to this file
      await api.post(`/tasks/${taskId}/versions-from-file`, {
        version_number: versionNumber,
        note: `Version créée à partir du fichier: ${file.name}`,
        file_id: file.id
      });
      
      toast.success(`Version V${versionNumber} créée avec succès`);
      fetchTaskData();
    } catch (error) {
      console.error('Error creating version:', error);
      toast.error('Erreur lors de la création de la version');
    }
  };

  const handleMarkComplete = async () => {
    try {
      await api.put(`/tasks/${taskId}`, { status: 'completed' });
      toast.success('Tâche terminée !');
      fetchTaskData();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const getStatusBadge = (status) => {
    const config = {
      'pending': { label: 'À faire', className: 'bg-slate-100 text-slate-700', icon: Clock },
      'in_progress': { label: 'En cours', className: 'bg-blue-100 text-blue-700', icon: Play },
      'submitted': { label: 'Soumise', className: 'bg-purple-100 text-purple-700', icon: Send },
      'agency_validated': { label: 'Validé agence', className: 'bg-cyan-100 text-cyan-700', icon: CheckCircle2 },
      'client_validated': { label: 'Validé client', className: 'bg-green-100 text-green-700', icon: CheckCircle2 },
      'completed': { label: 'Terminée', className: 'bg-emerald-100 text-emerald-700', icon: Check },
      'rejected': { label: 'Rejetée', className: 'bg-red-100 text-red-700', icon: XCircle }
    };
    const c = config[status] || config['pending'];
    const Icon = c.icon;
    return (
      <Badge className={cn("flex items-center gap-1", c.className)}>
        <Icon className="w-3 h-3" />
        {c.label}
      </Badge>
    );
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#f8fafc]">
        <Sidebar collapsed={collapsed} setCollapsed={setCollapsed} mobileOpen={mobileOpen} setMobileOpen={setMobileOpen} />
        <Header collapsed={collapsed} setMobileOpen={setMobileOpen} />
        <main className={cn("transition-all duration-300 px-4 md:px-6 pb-6", collapsed ? "md:ml-20" : "md:ml-64")} style={{ paddingTop: "130px" }}>
          <div className="flex items-center justify-center h-64">
            <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
          </div>
        </main>
      </div>
    );
  }

  if (!task) {
    return (
      <div className="min-h-screen bg-[#f8fafc]">
        <Sidebar collapsed={collapsed} setCollapsed={setCollapsed} mobileOpen={mobileOpen} setMobileOpen={setMobileOpen} />
        <Header collapsed={collapsed} setMobileOpen={setMobileOpen} />
        <main className={cn("transition-all duration-300 px-4 md:px-6 pb-6", collapsed ? "md:ml-20" : "md:ml-64")} style={{ paddingTop: "130px" }}>
          <div className="text-center py-12">
            <AlertCircle className="w-12 h-12 mx-auto mb-4 text-slate-300" />
            <p className="text-slate-500">Tâche non trouvée</p>
            <Button variant="outline" className="mt-4" onClick={() => navigate(-1)}>
              <ArrowLeft className="w-4 h-4 mr-2" /> Retour
            </Button>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f8fafc]">
      <Sidebar collapsed={collapsed} setCollapsed={setCollapsed} mobileOpen={mobileOpen} setMobileOpen={setMobileOpen} />
      <Header collapsed={collapsed} setMobileOpen={setMobileOpen} />
      <main className={cn("transition-all duration-300 px-4 md:px-6 pb-6", collapsed ? "md:ml-20" : "md:ml-64")} style={{ paddingTop: "130px" }}>
        
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 mb-6">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate(-1)} className="shrink-0">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex-1 min-w-0">
              <div className="flex flex-wrap items-center gap-2 mb-1">
                <h1 className="text-lg sm:text-2xl font-bold truncate" style={{ color: theme.colors?.primary }}>
                  {task.name || task.title}
                </h1>
                {getStatusBadge(task.status)}
              </div>
              <p className="text-slate-500 text-xs sm:text-sm truncate">
                {project?.name && <span>Projet: {project.name} • </span>}
                Créée le {formatDate(task.created_at)}
              </p>
            </div>
          </div>
          
          {/* Validation Badges & Version Counter - Mobile responsive */}
          <div className="flex items-center gap-2 flex-wrap pl-11 sm:pl-0">
            <div className={cn(
              "flex items-center gap-1 px-2 sm:px-3 py-1 sm:py-1.5 rounded-full text-xs sm:text-sm font-medium",
              task.agency_validated 
                ? "bg-cyan-100 text-cyan-700" 
                : "bg-slate-100 text-slate-400"
            )}>
              <CheckCircle2 className="w-3 h-3 sm:w-4 sm:h-4" />
              <span className="hidden sm:inline">Agence</span>
              <span className="sm:hidden">Ag.</span>
            </div>
            <div className={cn(
              "flex items-center gap-1 px-2 sm:px-3 py-1 sm:py-1.5 rounded-full text-xs sm:text-sm font-medium",
              task.client_validated 
                ? "bg-green-100 text-green-700" 
                : "bg-slate-100 text-slate-400"
            )}>
              <CheckCircle2 className="w-3 h-3 sm:w-4 sm:h-4" />
              <span className="hidden sm:inline">Client</span>
              <span className="sm:hidden">Cl.</span>
            </div>
            <div className="flex items-center gap-1 sm:gap-2 px-2 sm:px-3 py-1 sm:py-1.5 bg-purple-100 rounded-full">
              <Layers className="w-3 h-3 sm:w-4 sm:h-4 text-purple-600" />
              <span className="text-xs sm:text-sm font-medium text-purple-700">
                {versions.length} v.
              </span>
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Task Description */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Description</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-slate-600 whitespace-pre-wrap">
                  {task.description || 'Aucune description'}
                </p>
              </CardContent>
            </Card>

            {/* Tabs: Chat / Versions / Files */}
            <Tabs defaultValue="chat" className="space-y-4">
              <TabsList className="w-full grid grid-cols-3 h-auto">
                <TabsTrigger value="chat" className="gap-1 sm:gap-2 text-xs sm:text-sm py-2 px-1 sm:px-3">
                  <MessageSquare className="w-3 h-3 sm:w-4 sm:h-4" />
                  <span className="hidden sm:inline">Discussion</span>
                  <span className="sm:hidden">Chat</span>
                  <span className="text-[10px] sm:text-xs">({messages.length})</span>
                </TabsTrigger>
                <TabsTrigger value="versions" className="gap-1 sm:gap-2 text-xs sm:text-sm py-2 px-1 sm:px-3">
                  <Layers className="w-3 h-3 sm:w-4 sm:h-4" />
                  <span className="hidden sm:inline">Versions</span>
                  <span className="sm:hidden">V.</span>
                  <span className="text-[10px] sm:text-xs">({versions.length})</span>
                </TabsTrigger>
                <TabsTrigger value="files" className="gap-1 sm:gap-2 text-xs sm:text-sm py-2 px-1 sm:px-3">
                  <Paperclip className="w-3 h-3 sm:w-4 sm:h-4" />
                  <span className="hidden sm:inline">Fichiers</span>
                  <span className="sm:hidden">Fich.</span>
                  <span className="text-[10px] sm:text-xs">({files.length})</span>
                </TabsTrigger>
              </TabsList>

              {/* Chat Tab */}
              <TabsContent value="chat">
                <Card>
                  <CardContent className="p-0">
                    {/* Messages */}
                    <div className="h-[400px] overflow-y-auto p-4 space-y-4">
                      {messages.length === 0 ? (
                        <div className="text-center py-12 text-slate-400">
                          <MessageSquare className="w-10 h-10 mx-auto mb-2 opacity-50" />
                          <p>Aucun message</p>
                        </div>
                      ) : (
                        messages.map((msg, idx) => (
                          <div key={idx} className={cn(
                            "flex gap-3",
                            msg.user_id === user?.id ? "flex-row-reverse" : ""
                          )}>
                            <Avatar name={msg.user_name || 'U'} size="sm" />
                            <div className={cn(
                              "max-w-[70%] rounded-lg p-3",
                              msg.user_id === user?.id 
                                ? "bg-amber-100 text-amber-900" 
                                : "bg-slate-100 text-slate-800"
                            )}>
                              <p className="text-xs font-medium mb-1">{msg.user_name}</p>
                              <p className="text-sm">{msg.content}</p>
                              <p className="text-xs opacity-60 mt-1">{formatDate(msg.created_at)}</p>
                            </div>
                          </div>
                        ))
                      )}
                      <div ref={messagesEndRef} />
                    </div>
                    
                    {/* Input */}
                    <div className="p-4 border-t flex gap-2">
                      <Input
                        placeholder="Votre message..."
                        value={newMessage}
                        onChange={(e) => setNewMessage(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                      />
                      <Button 
                        onClick={handleSendMessage} 
                        disabled={sendingMessage || !newMessage.trim()}
                        style={{ backgroundColor: theme.colors?.accent }}
                      >
                        {sendingMessage ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Versions Tab */}
              <TabsContent value="versions">
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between">
                    <CardTitle className="text-base">Historique des versions</CardTitle>
                    {isMember && (
                      <Button size="sm" onClick={() => setShowVersionDialog(true)} style={{ backgroundColor: theme.colors?.accent }}>
                        <Plus className="w-4 h-4 mr-1" />
                        Soumettre une version
                      </Button>
                    )}
                  </CardHeader>
                  <CardContent>
                    {versions.length === 0 ? (
                      <div className="text-center py-8 text-slate-400">
                        <Layers className="w-10 h-10 mx-auto mb-2 opacity-50" />
                        <p>Aucune version soumise</p>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {versions.map((version, idx) => (
                          <div key={idx} className="p-4 border rounded-lg">
                            <div className="flex items-center justify-between mb-2">
                              <Badge className="bg-purple-100 text-purple-700">
                                Version {versions.length - idx}
                              </Badge>
                              <span className="text-xs text-slate-500">{formatDate(version.created_at)}</span>
                            </div>
                            <p className="text-sm text-slate-600 mb-2">{version.note || 'Pas de note'}</p>
                            {version.files && version.files.length > 0 && (
                              <div className="flex flex-wrap gap-2">
                                {version.files.map((file, fidx) => (
                                  <a
                                    key={fidx}
                                    href={file.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex items-center gap-1 px-2 py-1 bg-slate-100 rounded text-xs hover:bg-slate-200"
                                  >
                                    <Paperclip className="w-3 h-3" />
                                    {file.name}
                                  </a>
                                ))}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Files Tab */}
              <TabsContent value="files">
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between">
                    <CardTitle className="text-base">Fichiers de la tâche</CardTitle>
                    {(isAgency || isMember) && (
                      <div>
                        <input
                          type="file"
                          ref={fileInputRef}
                          className="hidden"
                          onChange={handleFileUpload}
                        />
                        <Button 
                          size="sm" 
                          onClick={() => fileInputRef.current?.click()}
                          disabled={uploadingFile}
                          style={{ backgroundColor: theme.colors?.accent }}
                        >
                          {uploadingFile ? (
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          ) : (
                            <Upload className="w-4 h-4 mr-2" />
                          )}
                          Ajouter un fichier
                        </Button>
                      </div>
                    )}
                  </CardHeader>
                  <CardContent>
                    {files.length === 0 ? (
                      <div className="text-center py-8 text-slate-400">
                        <Paperclip className="w-10 h-10 mx-auto mb-2 opacity-50" />
                        <p>Aucun fichier</p>
                        {(isAgency || isMember) && (
                          <Button 
                            variant="outline" 
                            className="mt-3"
                            onClick={() => fileInputRef.current?.click()}
                          >
                            <Upload className="w-4 h-4 mr-2" />
                            Uploader un fichier
                          </Button>
                        )}
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {files.map((file, idx) => (
                          <div key={idx} className="flex flex-col sm:flex-row sm:items-center justify-between p-3 border rounded-lg gap-3">
                            <div className="flex items-center gap-3">
                              {file.type?.startsWith('image') ? (
                                <Image className="w-8 h-8 text-blue-500 shrink-0" />
                              ) : (
                                <FileText className="w-8 h-8 text-slate-400 shrink-0" />
                              )}
                              <div className="min-w-0">
                                <p className="font-medium text-sm truncate">{file.name}</p>
                                <p className="text-xs text-slate-500">{formatDate(file.uploaded_at)}</p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2 flex-wrap">
                              {/* Validation status */}
                              <div className="flex items-center gap-1">
                                <span className={cn(
                                  "w-6 h-6 rounded-full flex items-center justify-center text-xs",
                                  file.agency_validated ? "bg-cyan-100 text-cyan-700" : "bg-slate-100 text-slate-400"
                                )}>
                                  A
                                </span>
                                <span className={cn(
                                  "w-6 h-6 rounded-full flex items-center justify-center text-xs",
                                  file.client_validated ? "bg-green-100 text-green-700" : "bg-slate-100 text-slate-400"
                                )}>
                                  C
                                </span>
                              </div>
                              
                              {/* Validation buttons */}
                              {isAgency && !file.agency_validated && (
                                <Button size="sm" variant="outline" onClick={() => handleValidateFile(file.id, true, 'agency')}>
                                  <Check className="w-3 h-3 mr-1" /> Valider
                                </Button>
                              )}
                              {isClient && file.agency_validated && !file.client_validated && (
                                <div className="flex gap-1">
                                  <Button size="sm" variant="outline" className="text-green-600" onClick={() => handleValidateFile(file.id, true, 'client')}>
                                    <Check className="w-3 h-3" />
                                  </Button>
                                  <Button size="sm" variant="outline" className="text-red-600" onClick={() => handleValidateFile(file.id, false, 'client')}>
                                    <X className="w-3 h-3" />
                                  </Button>
                                </div>
                              )}
                              {/* Agency can validate on behalf of client */}
                              {isAgency && file.agency_validated && !file.client_validated && (
                                <Button size="sm" variant="outline" className="text-green-600" onClick={() => handleValidateFile(file.id, true, 'client')}>
                                  <Check className="w-3 h-3 mr-1" /> Client OK
                                </Button>
                              )}
                              
                              {/* Create version from file */}
                              {(isAgency || isMember) && !file.version_number && (
                                <Button 
                                  size="sm" 
                                  variant="outline"
                                  className="text-purple-600 border-purple-200 hover:bg-purple-50"
                                  onClick={() => handleCreateVersionFromFile(file)}
                                >
                                  <Layers className="w-3 h-3 mr-1" /> V{versions.length + 1}
                                </Button>
                              )}
                              
                              {/* Show version badge if file is a version */}
                              {file.version_number && (
                                <Badge className="bg-purple-100 text-purple-700">
                                  V{file.version_number}
                                </Badge>
                              )}
                              
                              <a href={file.url} target="_blank" rel="noopener noreferrer">
                                <Button size="sm" variant="ghost">
                                  <Download className="w-4 h-4" />
                                </Button>
                              </a>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Task Info */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Informations</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-500">Assigné à</span>
                  <div className="flex items-center gap-2">
                    <Avatar name={task.assignee_name || 'N/A'} size="xs" />
                    <span className="text-sm font-medium">{task.assignee_name || 'Non assigné'}</span>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-500">Échéance</span>
                  <span className="text-sm font-medium">{formatDate(task.due_date)}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-500">Priorité</span>
                  <Badge className={cn(
                    task.priority === 'high' ? 'bg-red-100 text-red-700' :
                    task.priority === 'medium' ? 'bg-amber-100 text-amber-700' :
                    'bg-slate-100 text-slate-700'
                  )}>
                    {task.priority === 'high' ? 'Haute' : task.priority === 'medium' ? 'Moyenne' : 'Normale'}
                  </Badge>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-500">Durée estimée</span>
                  <span className="text-sm font-medium">{task.estimated_hours || 0}h</span>
                </div>
                
                {/* Scheduled date */}
                {task.scheduled_date && (
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-500">Planifié le</span>
                    <div className="flex items-center gap-1 text-sm">
                      <Calendar className="w-3 h-3 text-indigo-600" />
                      <span className="font-medium text-indigo-600">
                        {new Date(task.scheduled_date).toLocaleDateString('fr-FR')} 
                        {task.scheduled_time && ` à ${task.scheduled_time}`}
                      </span>
                    </div>
                  </div>
                )}
                
                {/* Schedule button for agency/project managers */}
                {(isAgency || isMember) && task.assigned_to && task.estimated_hours > 0 && (
                  <Button 
                    variant="outline"
                    className="w-full mt-2 border-indigo-300 text-indigo-700 hover:bg-indigo-50"
                    onClick={() => setShowSchedulerDialog(true)}
                  >
                    <CalendarPlus className="w-4 h-4 mr-2" />
                    {task.scheduled_date ? 'Replanifier' : 'Planifier sur l\'agenda'}
                  </Button>
                )}
                
                {/* Timer button for team members */}
                {isMember && task.assigned_to === user?.id && task.status !== 'completed' && (
                  <div className="mt-3 pt-3 border-t">
                    {hasActiveTimer ? (
                      <Button 
                        className="w-full bg-red-500 hover:bg-red-600"
                        onClick={handleStopTimer}
                        disabled={timerLoading}
                      >
                        {timerLoading ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <Square className="w-4 h-4 mr-2" />
                        )}
                        Arrêter le chrono
                      </Button>
                    ) : (
                      <Button 
                        className="w-full bg-green-500 hover:bg-green-600"
                        onClick={handleStartTimer}
                        disabled={timerLoading}
                      >
                        {timerLoading ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <Play className="w-4 h-4 mr-2" />
                        )}
                        Démarrer le chrono
                      </Button>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Validation Actions */}
            <Card className="border-2" style={{ borderColor: theme.colors?.accent }}>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Actions de validation</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {/* Agency validation */}
                {isAgency && !task.agency_validated && task.status !== 'pending' && (
                  <Button 
                    className="w-full bg-cyan-600 hover:bg-cyan-700"
                    onClick={handleAgencyValidate}
                  >
                    <CheckCircle2 className="w-4 h-4 mr-2" />
                    Valider (Agence)
                  </Button>
                )}
                
                {task.agency_validated && !task.client_validated && (
                  <div className="p-3 bg-cyan-50 rounded-lg text-center">
                    <CheckCircle2 className="w-5 h-5 text-cyan-600 mx-auto mb-1" />
                    <p className="text-sm text-cyan-700 font-medium">Validé par l'agence</p>
                    <p className="text-xs text-cyan-600">En attente validation client</p>
                  </div>
                )}
                
                {/* Agency can validate on behalf of client */}
                {isAgency && task.agency_validated && !task.client_validated && (
                  <Button 
                    className="w-full bg-green-600 hover:bg-green-700"
                    onClick={() => setShowClientValidationModal(true)}
                  >
                    <CheckCircle2 className="w-4 h-4 mr-2" />
                    Valider au nom du client
                  </Button>
                )}
                
                {/* Client validation */}
                {isClient && task.agency_validated && !task.client_validated && (
                  <div className="space-y-2">
                    <Button 
                      className="w-full bg-green-600 hover:bg-green-700"
                      onClick={() => handleClientValidate(true)}
                    >
                      <Check className="w-4 h-4 mr-2" />
                      Approuver
                    </Button>
                    <Button 
                      variant="outline"
                      className="w-full text-red-600 border-red-300 hover:bg-red-50"
                      onClick={() => handleClientValidate(false)}
                    >
                      <X className="w-4 h-4 mr-2" />
                      Demander des révisions
                    </Button>
                  </div>
                )}
                
                {task.client_validated && (
                  <div className="p-3 bg-green-50 rounded-lg text-center">
                    <CheckCircle2 className="w-5 h-5 text-green-600 mx-auto mb-1" />
                    <p className="text-sm text-green-700 font-medium">Validé par le client</p>
                    {task.client_validated_by_agency && (
                      <p className="text-xs text-green-600">
                        (Validé par l'agence - {task.client_validation_channel === 'email' ? 'Email' : 
                          task.client_validation_channel === 'phone' ? 'Téléphone' : 'En agence'})
                      </p>
                    )}
                  </div>
                )}
                
                {/* Complete task */}
                {isAgency && task.agency_validated && task.client_validated && task.status !== 'completed' && (
                  <Button 
                    className="w-full bg-emerald-600 hover:bg-emerald-700"
                    onClick={handleMarkComplete}
                  >
                    <Check className="w-4 h-4 mr-2" />
                    Marquer comme terminée
                  </Button>
                )}
                
                {task.status === 'completed' && (
                  <div className="p-3 bg-emerald-50 rounded-lg text-center">
                    <Check className="w-6 h-6 text-emerald-600 mx-auto mb-1" />
                    <p className="text-sm text-emerald-700 font-medium">Tâche terminée</p>
                    <p className="text-xs text-emerald-600">{formatDate(task.completed_at)}</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Submit version (for members) */}
            {isMember && task.status !== 'completed' && (
              <Card>
                <CardContent className="p-4">
                  <Button 
                    className="w-full"
                    style={{ backgroundColor: theme.colors?.accent }}
                    onClick={() => setShowVersionDialog(true)}
                  >
                    <Layers className="w-4 h-4 mr-2" />
                    Soumettre une version
                  </Button>
                </CardContent>
              </Card>
            )}
          </div>
        </div>

        {/* Submit Version Dialog */}
        <Dialog open={showVersionDialog} onOpenChange={setShowVersionDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Soumettre une nouvelle version</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Note / Description des changements</Label>
                <Textarea
                  placeholder="Décrivez les modifications apportées..."
                  value={versionNote}
                  onChange={(e) => setVersionNote(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label>Fichiers (optionnel)</Label>
                <Input
                  type="file"
                  multiple
                  onChange={(e) => setVersionFiles(Array.from(e.target.files))}
                />
                {versionFiles.length > 0 && (
                  <p className="text-xs text-slate-500">{versionFiles.length} fichier(s) sélectionné(s)</p>
                )}
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowVersionDialog(false)}>
                Annuler
              </Button>
              <Button 
                onClick={handleSubmitVersion} 
                disabled={submittingVersion}
                style={{ backgroundColor: theme.colors?.accent }}
              >
                {submittingVersion ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                Soumettre
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Client Validation by Agency Modal */}
        <Dialog open={showClientValidationModal} onOpenChange={setShowClientValidationModal}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5 text-green-600" />
                Valider au nom du client
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <p className="text-sm text-slate-600">
                Le client a donné son accord pour valider cette tâche. Indiquez comment il vous a confirmé :
              </p>
              
              <div className="space-y-3">
                <Label>Canal de validation</Label>
                <div className="grid grid-cols-3 gap-2">
                  <Button
                    type="button"
                    variant={validationChannel === 'email' ? 'default' : 'outline'}
                    className={cn(
                      "flex flex-col items-center gap-1 h-auto py-3",
                      validationChannel === 'email' && "bg-green-600 hover:bg-green-700"
                    )}
                    onClick={() => setValidationChannel('email')}
                  >
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                    <span className="text-xs">Email</span>
                  </Button>
                  <Button
                    type="button"
                    variant={validationChannel === 'phone' ? 'default' : 'outline'}
                    className={cn(
                      "flex flex-col items-center gap-1 h-auto py-3",
                      validationChannel === 'phone' && "bg-green-600 hover:bg-green-700"
                    )}
                    onClick={() => setValidationChannel('phone')}
                  >
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                    </svg>
                    <span className="text-xs">Téléphone</span>
                  </Button>
                  <Button
                    type="button"
                    variant={validationChannel === 'in_agency' ? 'default' : 'outline'}
                    className={cn(
                      "flex flex-col items-center gap-1 h-auto py-3",
                      validationChannel === 'in_agency' && "bg-green-600 hover:bg-green-700"
                    )}
                    onClick={() => setValidationChannel('in_agency')}
                  >
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                    </svg>
                    <span className="text-xs">En agence</span>
                  </Button>
                </div>
              </div>
              
              <div className="space-y-2">
                <Label>Note (optionnel)</Label>
                <Textarea
                  placeholder="Ajoutez une note si nécessaire..."
                  value={validationNote}
                  onChange={(e) => setValidationNote(e.target.value)}
                  rows={2}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowClientValidationModal(false)}>
                Annuler
              </Button>
              <Button 
                onClick={handleClientValidationByAgency} 
                disabled={validatingOnBehalf}
                className="bg-green-600 hover:bg-green-700"
              >
                {validatingOnBehalf ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
                Confirmer la validation
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        
        {/* Task Scheduler Dialog */}
        {task && (
          <TaskSchedulerDialog
            open={showSchedulerDialog}
            onOpenChange={setShowSchedulerDialog}
            task={task}
            memberId={task.assigned_to}
            memberName={task.assignee_name}
            onScheduled={() => fetchTaskData()}
            theme={theme}
          />
        )}
      </main>
    </div>
  );
};

export default TaskDetailPage;
